import { useState } from "react";
import type { StageTrace, TraceError } from "../../lib/traceApi";
import { LayerRow } from "./LayerRow";
import {
  useTraceFilter,
  matchesQuery,
  subCallSearchable,
  bridgeStepSearchable,
  errorSearchable,
} from "./TraceFilterContext";
import { Spinner } from "./Spinner";
import { useTraceLive } from "./TraceLiveContext";

// Map the trace outcome string to a short admin-trace badge. Default for
// any unrecognized non-success outcome is "FAIL" (was "WAIT" pre-fix —
// which misleadingly suggested the stage was still in-flight even after
// it had ended with a recorded outcome). "WAIT" is reserved for genuinely
// in-flight stages (empty outcome string).
function badgeForOutcome(outcome: string): {
  label: string;
  className: string;
} {
  switch (outcome) {
    case "success":
      return { label: "OK", className: "text-emerald-300" };
    case "no_op":
      return { label: "NOOP", className: "text-ink-400" };
    case "aborted_panic":
      return { label: "PANIC", className: "text-rose-400" };
    case "aborted":
      return { label: "ABORT", className: "text-rose-300" };
    case "aborted_validator":
      return { label: "FAIL", className: "text-rose-300" };
    case "aborted_apply":
      return { label: "FAIL", className: "text-rose-300" };
    case "":
      return { label: "WAIT", className: "text-ink-500" };
    default:
      // Future-proof fallthrough: any new "aborted_*" outcome shows as
      // FAIL rather than WAIT. Unknown non-aborted outcomes get an
      // explicit "???" so a drift is visible rather than silent.
      return outcome.startsWith("aborted")
        ? { label: "FAIL", className: "text-rose-300" }
        : { label: "???", className: "text-amber-300" };
  }
}

export function StageRow({
  stage,
  errorsOnly,
  errors,
}: {
  stage: StageTrace;
  errorsOnly: boolean;
  // Per-stage filtered slice of trace.errors. The caller (TraceBody) splits
  // the flat trace.errors[] list across stage rows so each row owns the
  // errors that pertain to it. Filtering is by `stage` string match;
  // attempt binding is by parsing the "attempt N: ..." prefix that
  // RecordTraceError sites use in job_runner.go.
  errors?: TraceError[];
}) {
  const [open, setOpen] = useState(true);
  const badge = badgeForOutcome(stage.outcome);
  const { live } = useTraceLive();

  const durationMs = stage.completed_at
    ? new Date(stage.completed_at).getTime() - new Date(stage.started_at).getTime()
    : 0;
  const layers = stage.layers ?? [];
  const subCallCount = layers.flatMap((l) => l.sub_calls ?? []).length;
  const stageErrors = (errors ?? []).filter((e) => e.stage === stage.stage);
  // Bind errors to this specific stage row by matching the "attempt N:"
  // prefix in detail to stage.attempt. When stage.attempt is 0 (non-retried
  // stages: analyze/decide/synthetic) we own every error whose detail
  // doesn't carry an attempt prefix.
  const myErrors = stageErrors.filter((e) => {
    const m = e.detail.match(/^attempt (\d+):/);
    if (!m) return (stage.attempt ?? 0) === 0;
    return parseInt(m[1], 10) === (stage.attempt ?? 0);
  });

  if (errorsOnly && stage.outcome === "success") {
    const layerHasError = layers.some((l) =>
      (l.sub_calls ?? []).some((sc) => sc.outcome !== "success"),
    );
    if (!layerHasError && myErrors.length === 0) return null;
  }

  // Tree-wide filter: if a query is set, hide this stage unless something
  // in its subtree (sub-call, bridge step, or error) matches. Stage name
  // counts too so "decide" filters to just the DECIDE stage.
  const { query } = useTraceFilter();
  if (query !== "") {
    const stageMatches = matchesQuery(query, [stage.stage]);
    const errorMatches = myErrors.some((e) =>
      matchesQuery(query, errorSearchable(e)),
    );
    const subtreeMatches = layers.some((l) => {
      const subMatch = (l.sub_calls ?? []).some((sc) =>
        matchesQuery(query, subCallSearchable(sc)),
      );
      const bridgeMatch = (l.bridge?.steps ?? []).some((step) =>
        matchesQuery(query, bridgeStepSearchable(step)),
      );
      const layerNameMatch = matchesQuery(query, [l.name]);
      return subMatch || bridgeMatch || layerNameMatch;
    });
    if (!stageMatches && !errorMatches && !subtreeMatches) return null;
  }

  // "attempt N" label rendered next to the stage name when the runner
  // tagged this row. Distinguishes the three CREATE rows from each other
  // (otherwise they all read as "Stage create" with no disambiguator).
  const attemptLabel =
    stage.attempt && stage.attempt > 0 ? ` · attempt ${stage.attempt}` : "";

  return (
    <div className="border-l-2 border-gray-700 pl-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full text-left hover:bg-gray-800 px-2 py-1"
      >
        <span className="w-6 text-gray-500">{open ? "▼" : "▶"}</span>
        {stage.outcome === "" ? (
          live ? (
            <Spinner className="w-16 text-xs font-mono text-amber-300" />
          ) : (
            // Terminal run, stage never sealed (no EndStage) — it didn't
            // finish. "WAIT" would misread as "still queued"; this is
            // where the run stopped.
            <span className="w-16 text-[10px] font-mono text-amber-300">INCOMPLETE</span>
          )
        ) : (
          <span className={`w-16 text-xs font-mono ${badge.className}`}>{badge.label}</span>
        )}
        <span className="font-mono font-bold">Stage {stage.stage}{attemptLabel}</span>
        <span className="text-gray-400 text-sm ml-auto">
          {(durationMs / 1000).toFixed(1)}s · {layers.length} layers · {subCallCount} sub-calls · {stage.tx_ops ?? 0} ops · ${(stage.total_cost_usd ?? 0).toFixed(3)}
        </span>
      </button>
      {open && (
        <div className="space-y-1 mt-1 ml-4">
          {/* Stage-attempt-bound errors rendered prominently at the top
              so an operator opening the stage sees the failure context
              before scanning sub-call / bridge rows. Bordered panel with
              a heading instead of a thin <ul> — the previous styling was
              too easy to overlook for users scanning for "what failed". */}
          {myErrors.length > 0 && (
            <div className="ml-2 mb-3 border border-rose-700 bg-rose-950/40 rounded p-2">
              <div className="text-rose-300 font-bold text-xs mb-1">
                {myErrors.length} failure{myErrors.length === 1 ? "" : "s"} at this stage{stage.attempt ? ` (attempt ${stage.attempt})` : ""}
              </div>
              <div className="text-rose-200/70 text-[10px] mb-2">
                Captured by the runner AFTER the DAG completed (post-DAG validators, ApplyCreateDelta, supersession/coverage_claim applies). The
                individual sub-calls below all passed their per-call validation — the failure is at this higher scope.
              </div>
              <ul className="space-y-1">
                {myErrors.map((e, i) => (
                  <li
                    key={i}
                    className="text-xs font-mono bg-rose-950/60 border-l-2 border-rose-600 pl-2 py-1"
                  >
                    <span className="text-rose-400">[{e.source}/{e.code}]</span>{" "}
                    <span className="text-rose-100">{e.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {layers.map((layer, i) => (
            <LayerRow
              key={i}
              layer={layer}
              errorsOnly={errorsOnly}
              parentStageErrors={myErrors}
            />
          ))}
        </div>
      )}
    </div>
  );
}
